import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Check, Mail, ArrowLeft, Quote } from 'lucide-react';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validateEmail = (value) => {
    if (!value?.trim()) return 'Please enter your email address.';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(value?.trim())) return 'Please enter a valid email address.';
    return '';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      // Try the edge function first (sends branded email via Resend)
      const { data: sessionData } = await supabase?.auth?.getSession();
      const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;

      const response = await fetch(`${supabaseUrl}/functions/v1/send-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
          'apikey': anonKey,
        },
        body: JSON.stringify({ email: email?.trim() }),
      });

      if (!response?.ok) {
        // Fallback to Supabase built-in reset
        const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(email?.trim(), {
          redirectTo: `${window.location?.origin}/reset-password`,
        });
        if (resetError) throw resetError;
      }

      setSuccess(true);
    } catch (err) {
      console.error('Password reset error:', err);
      // Fallback: use Supabase built-in
      try {
        const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(email?.trim(), {
          redirectTo: `${window.location?.origin}/reset-password`,
        });
        if (resetError) throw resetError;
        setSuccess(true);
      } catch (fallbackErr) {
        setError(fallbackErr?.message || 'Failed to send reset email. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative py-8 px-4 sm:px-6 lg:px-8">
      {/* Background */}
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/assets/images/FortifiedMindSplash1-1769379659227.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          filter: 'brightness(0.7)'
        }}
      />
      <div className="absolute inset-0 bg-black/40" />

      <div className="relative z-10 w-full max-w-7xl mx-auto flex justify-start">
        <div className="w-full max-w-2xl space-y-6">
          {/* App Header */}
          <div className="text-center">
            <div className="mx-auto h-20 w-20 bg-white/50 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 shadow-2xl">
              <svg className="w-10 h-10 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight drop-shadow-2xl">FORTIFIED MIND</h1>
            <p className="text-white text-lg sm:text-xl mb-2 drop-shadow-lg font-semibold">Scripture Memorization & Reflection</p>
          </div>

          {/* Quote */}
          <div className="bg-primary/20 backdrop-blur-sm rounded-2xl shadow-2xl p-5 sm:p-6 text-white border border-white/20">
            <div className="flex items-start space-x-4">
              <Quote className="w-7 h-7 sm:w-8 sm:h-8 text-primary/80 flex-shrink-0 mt-1" />
              <div>
                <blockquote className="text-base sm:text-lg italic leading-relaxed mb-3">
                  "None but those who have <span className="font-bold text-warning bg-warning/20 px-1 rounded">fortified</span> the <span className="font-bold text-warning bg-warning/20 px-1 rounded">mind</span> with the truths of the Bible will stand through the last great conflict."
                </blockquote>
                <cite className="text-white/70 text-sm font-medium">— Great Controversy pg. 593</cite>
              </div>
            </div>
          </div>

          {/* Card */}
          <div className="backdrop-blur-md rounded-2xl shadow-2xl p-6 sm:p-8 space-y-5 border border-white/30 bg-[rgba(255,255,255,0.6)]">
            {/* Back link */}
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Sign In
            </Link>

            {/* Title */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Forgot Password?</h2>
              </div>
              <p className="text-sm text-foreground/70 leading-relaxed">
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>

            {/* Success State */}
            {success ? (
              <div className="space-y-4">
                <div className="bg-success/10 border border-success/30 rounded-xl p-5 flex items-start space-x-3">
                  <div className="w-8 h-8 bg-success/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-success" />
                  </div>
                  <div>
                    <p className="text-success font-semibold mb-1">Reset Link Sent!</p>
                    <p className="text-success/80 text-sm leading-relaxed">
                      If an account exists for <strong>{email}</strong>, you'll receive a password reset email shortly. Please check your inbox and spam folder.
                    </p>
                    <p className="text-success/70 text-xs mt-2">The link will expire in 1 hour.</p>
                  </div>
                </div>

                <Button
                  onClick={() => { setSuccess(false); setEmail(''); setError(''); }}
                  variant="outline"
                  className="w-full"
                >
                  Send Another Link
                </Button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-block w-full bg-primary text-primary-foreground font-bold text-base py-4 px-6 rounded-xl hover:opacity-90 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl touch-manipulation min-h-[52px] flex items-center justify-center"
                  >
                    Return to Sign In
                  </Link>
                </div>
              </div>
            ) : (
              <>
                {/* Error */}
                {error && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 flex items-center space-x-3">
                    <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                    <p className="text-destructive text-sm">{error}</p>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    floating
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e?.target?.value);
                      if (error) setError('');
                    }}
                    required
                    className="w-full text-base touch-manipulation"
                    autoComplete="email"
                    autoFocus
                  />

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-primary text-primary-foreground py-4 sm:py-3 rounded-xl hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 text-base font-semibold min-h-[52px] touch-manipulation"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center space-x-2">
                        <span className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
                        <span>Sending Reset Link...</span>
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Mail className="w-4 h-4" />
                        Send Reset Link
                      </span>
                    )}
                  </Button>
                </form>

                <div className="text-center pt-2 border-t border-border/40">
                  <p className="text-sm text-foreground/70">
                    Remember your password?{' '}
                    <Link to="/login" className="text-primary font-semibold hover:underline">
                      Sign In
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
